<script setup lang="ts">
import { dateRange } from '~/helpers/formatDateRange.js'
import { getPriceWithCurrency } from '~/helpers/priceHelper'
import { $dayjs } from '@/utils/dayjs'

import type { HouseDetails } from '~/types/house/types'

const { locale, t } = useI18n()
const { currency } = useUser()
const {
  booking,
  bookedDatesExceptChecks,
  fetchAndsetSelectedPeriod,
  setDates,
} = useHouseInquiryBooking()
const { isTreatment } = useExperiment('38576-house-question-v4')

$dayjs.locale(locale.value)

const { bookingSent = false, house } = defineProps<{
  bookingSent?: boolean
  house: HouseDetails
}>()

const emits = defineEmits<{
  'show-mobile-inquiry': [boolean]
  'show-mobile-booking': [boolean]
  'show-mobile-calendar': []
}>()

const dateRangeFormatted = computed(() =>
  dateRange({
    i18n: { locale, t },
    startDate: booking.value.startDate,
    endDate: booking.value.endDate,
  }),
)

const maxPrice = computed(() =>
  getPriceWithCurrency({
    price: house.maxPrice[currency.value],
    currency: currency.value,
    i18n: { locale },
  }),
)

const minPrice = computed(() =>
  getPriceWithCurrency({
    price: house.minPrice[currency.value],
    currency: currency.value,
    i18n: { locale },
  }),
)

const price = computed(() =>
  getPriceWithCurrency({
    price: booking.value.price.value ?? 0,
    currency: currency.value,
    i18n: { locale },
  }).concat(booking.value.price.definitivePrice ? '' : '*'),
)

const showReinsuranceBlock = computed(
  () => !booking.value.price.definitivePrice && booking.value.price.value,
)

// Check dates to enable (or show) / disable (or hide) the mobile book button
const showBookingBtn = computed(
  () =>
    house.onlineReservation &&
    periodBookableOnline.value &&
    validatePeriodAgainstBookedDates(
      booking.value,
      bookedDatesExceptChecks.value,
    ),
)
const periodBookableOnline = computed(() => {
  if (
    booking.value.startDate &&
    booking.value.endDate &&
    house.onlineReservation
  ) {
    return selectedPeriod.value?.periodBookableOnline || false
  }

  return true
})

// Check dates to enable/disable the mobile inquiry button
const dateIsIncorrectPeriod = computed(
  () =>
    selectedPeriod.value?.fullyCovered &&
    !selectedPeriod.value.allMinimumDurationValid,
)
const isPeriodInvalidAndPartial = computed(() => {
  if (
    selectedPeriod.value &&
    !selectedPeriod.value.fullyCovered &&
    !selectedPeriod.value.allMinimumDurationValid &&
    selectedPeriod.value.periods?.length > 0
  ) {
    return selectedPeriod.value.periods.some(
      (period) => !period.validMinimumDuration,
    )
  }
  return false
})
const blockedCalendar = computed(() => {
  if (dateIsIncorrectPeriod.value || isPeriodInvalidAndPartial.value) {
    return true
  }

  return false
})

const handleClickBooking = (calendar: boolean) => {
  emits('show-mobile-booking', calendar)
}

const handleClickInquiry = (calendar: boolean) => {
  emits('show-mobile-inquiry', calendar)
}
const handleClickModifyDates = () => {
  setDates(null, null)
  if (isTreatment.value) {
    emits('show-mobile-booking', true)
  } else if (house.onlineReservation) {
    emits('show-mobile-calendar')
  } else {
    emits('show-mobile-inquiry', true)
  }
}

const { data: selectedPeriod } = await useAsyncData(
  `selected-period-${house.id}`,
  async () =>
    fetchAndsetSelectedPeriod({
      currency: currency.value,
      houseId: Number(house.id),
      displayAvailabilities: !!house.displayAvailabilities,
      displayPrices: !!house.displayPrices,
      minPrice: house.minPrice,
      maxPrice: house.maxPrice,
    }),
  { watch: [() => booking.value.endDate, () => currency.value] },
)
</script>

<template>
  <div
    class="z-base shrink-0 items-center justify-between border-t border-gray-200 p-4"
  >
    <div
      v-if="bookingSent"
      class="flex w-full items-center justify-center gap-1 rounded bg-secondary-100 py-2 text-sm text-secondary-200"
    >
      <p class="m-0 font-bold">{{ $t('preOrder.bookingSent') }}</p>
      <BaseIcon name="check" />
    </div>

    <template v-else>
      <div class="mr-2 text-md">
        <p
          v-if="
            (!booking.price.isValid && booking.endDate) || !house.displayPrices
          "
          class="mb-2 flex justify-between"
          data-testid="mobile-inquiry__price-unavailable"
        >
          <span class="block font-bold">
            {{ $t('house.price_upon_request') }}
          </span>
          <button
            v-if="booking.endDate"
            type="button"
            class="underline"
            data-testid="mobile-inquiry__button-range-date"
            @click="handleClickModifyDates"
          >
            <span class="whitespace-nowrap">{{ dateRangeFormatted }}</span>
          </button>
        </p>

        <p v-else-if="booking.price.value" class="mb-2 flex justify-between">
          <span class="block font-bold">
            {{ price }}
          </span>

          <button
            class="underline"
            type="button"
            data-testid="mobile-inquiry__button-range-date"
            @click="handleClickModifyDates"
          >
            <span class="whitespace-nowrap">{{ dateRangeFormatted }}</span>
          </button>
        </p>

        <div v-else class="mb-2 flex">
          <p v-if="minPrice !== maxPrice" class="m-0">
            <span class="pr-1 font-bold">
              {{ $t('house.priceWeeklyRangeStrongFrom', { min: minPrice }) }}
              {{ $t('house.priceWeeklyRangeStrongTo', { max: maxPrice }) }}
            </span>
            <span>
              {{ $t('house.priceWeeklyRange') }}
            </span>
          </p>
          <p v-else class="m-0">
            <span class="pr-1 font-bold">
              {{ $t('search.price_nightly', { price: minPrice }) }}
            </span>
            <span>
              {{ $t('house.priceWeeklyRange') }}
            </span>
          </p>
        </div>
      </div>

      <div
        v-if="showReinsuranceBlock"
        :class="[
          'mb-1 text-xs',
          { 'order-last mt-1': !house.onlineReservation },
        ]"
      >
        {{ $t('inquiry.reinsurranceBlock[0]') }}
      </div>

      <div class="flex gap-4">
        <template v-if="!booking.submitted">
          <ABContainer flag-key="38576-house-question-v4">
            <template #control>
              <BaseButton
                class="min-w-[50%] !grow !text-md"
                color="secondary"
                data-testid="mobile-inquiry__button"
                :disabled="blockedCalendar"
                :variant="showBookingBtn ? 'outline' : 'btn'"
                @click="handleClickInquiry(!booking.endDate)"
              >
                {{ $t('house.request') }}
              </BaseButton>
              <BaseButton
                v-if="showBookingBtn"
                class="!w-1/2 !text-md"
                color="secondary"
                data-testid="mobile-booking__button"
                @click="handleClickBooking(!booking.endDate)"
              >
                {{ $t('preOrder.cta') }}
              </BaseButton>
            </template>

            <template #treatment>
              <BaseButton
                class="!w-1/2 !text-md"
                color="secondary"
                variant="outline"
                data-testid="mobile-inquiry__button"
                @click="handleClickInquiry(!booking.endDate)"
              >
                {{ $t('global.askAQuestion') }}
              </BaseButton>
              <BaseButton
                class="!w-1/2 !text-md"
                color="secondary"
                data-testid="mobile-booking__button"
                :disabled="!showBookingBtn"
                @click="handleClickBooking(!booking.endDate)"
              >
                {{ $t('preOrder.cta') }}
              </BaseButton>
            </template>
          </ABContainer>
        </template>
        <div v-else class="flex items-center justify-end text-secondary-400">
          <span class="mr-3 font-bold">
            {{ $t('inquiry.requestSent') }}
          </span>
          <BaseIcon name="check" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
@media (max-width: 420px) {
  :deep(.lc-btn--md) {
    @apply !px-2;
  }
}
</style>
